/**
 * The page "Process mining" (15 Oct 2026): TanStack Query hooks over the read routes of the
 * process-mining event log (`/process-mining/*`).
 *
 * The event log describes how a quotation is made today, before I2Q: from the shared
 * mailboxes and the read-only F&O copy, built at night or by hand, plus tool data that is
 * imported by hand as pseudonymised aggregates. Only the role `processmining` (capability
 * `see_process_mining`) may read it; every other session gets 403, an executive-only
 * session too.
 *
 * - `GET /process-mining/findings` (`getProcessMiningFindings`, 16 Oct 2026): the findings,
 *   the first tab. `GET /process-mining/cases?finding=<id>` lists the cases of one finding.
 * - `GET /process-mining/summary` (`getProcessMiningSummary`): Layer 1, the value stream.
 * - `GET /process-mining/map` (`getProcessMiningMap`): Layer 2, the process map.
 * - `GET /process-mining/variants` (`listProcessMiningVariants`): Layer 2, the variants.
 * - `GET /process-mining/areas` (`getProcessMiningAreas`): Layer 3, the area comparison.
 * - `GET /process-mining/tools` (`getProcessMiningTools`): Layer 4, the tools.
 * - `GET /process-mining/cases` (`listProcessMiningCases`): Layer 5, the case list.
 * - `GET /process-mining/cases/{case_key}` (`getProcessMiningCase`): Layer 5, one case.
 * - `GET /process-mining/baseline` (`getProcessMiningBaseline`): "Where I2Q helps".
 * - `GET /process-mining/runs` (`listProcessMiningRuns`): builds and imports.
 * - `GET /process-mining/value` (`getProcessMiningValue`, 16 Oct 2026, round 4): the summary,
 *   the speed and decision figures, the benchmarks, the weekly series and the I2Q scorecard.
 * - `GET /process-mining/case-export` (`exportProcessMiningCases`, 16 Oct 2026): the case list
 *   as CSV. The page links to it (`pmCaseExportHref`); the browser downloads the file.
 *
 * - `GET /process-mining/report` (`downloadProcessMiningReport`, 17 Oct 2026): the report of
 *   one audience as a Word report or a PowerPoint deck. The page links to it (`pmReportHref`).
 * - `GET /process-mining/report/status` (`getProcessMiningReportStatus`): the text state of
 *   that report. `POST /process-mining/report/texts` (`writeProcessMiningReportTexts`) asks the
 *   server to write the text in the background.
 *
 * Every figure route takes the shared filter `qtype` (`Sales` or `Project`, round 4) and a
 * period: the window of the last N days, or `from` and `to` when both are set.
 *
 * The anchors route and the import route serve the by-hand extraction, not the page: no hook.
 *
 * The first part of each query key is the request key of the usage vocabulary
 * (`process-mining-summary`, ...). A value the server could not measure arrives as `null`
 * with a status; the page shows the status word, never 0.
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { type ApiError } from "./client";
import {
  type DownloadProcessMiningReportData,
  getProcessMiningAreas,
  getProcessMiningBaseline,
  getProcessMiningCase,
  getProcessMiningFindings,
  getProcessMiningMap,
  getProcessMiningReportStatus,
  getProcessMiningSummary,
  getProcessMiningTools,
  getProcessMiningValue,
  listProcessMiningCases,
  listProcessMiningRuns,
  listProcessMiningVariants,
  type PmActionTrack,
  type PmAreaComparison,
  type PmBaseline,
  type PmCaseList,
  type PmCaseRow,
  type PmCaseTimeline,
  type PmCausePartRow,
  type PmCoreModel,
  type PmDecision,
  type PmFinding,
  type PmFindingEvidence,
  type PmFindings,
  type PmFormWindowIn,
  type PmGoldenStep,
  type PmGoldenUse,
  type PmMap,
  type PmMapNode,
  type PmMeasure,
  type PmPain,
  type PmPainCause,
  type PmReportStatus,
  type PmReportTextsRequest,
  type PmReworkPart,
  type PmRuns,
  type PmScoreRow,
  type PmSummary,
  type PmTools,
  type PmTrend,
  type PmValue,
  type PmVariants,
  writeProcessMiningReportTexts,
} from "./generated";

export type {
  PmActionTrack,
  PmAreaComparison,
  PmAreaFormCompare,
  PmAreaRow,
  PmAssumption,
  PmBaseline,
  PmBaselineOfRecord,
  PmBaselineRow,
  PmBenchArea,
  PmBenchmark,
  PmBuildRun,
  PmCaseList,
  PmCaseRow,
  PmCaseTimeline,
  PmCausePartRow,
  PmCoreModel,
  PmDecision,
  PmDuration,
  PmDwell,
  PmEffect,
  PmFinding,
  PmFindingCoverage,
  PmFindingDrill,
  PmFindingEvidence,
  PmFindings,
  PmFindingSize,
  PmFindingSubject,
  PmFlowLink,
  PmFreshness,
  PmFunnelStep,
  PmGap,
  PmGoldenStep,
  PmGoldenUse,
  PmHeadline,
  PmHelpStep,
  PmImportRow,
  PmMap,
  PmMapEdge,
  PmMapNode,
  PmMeasure,
  PmMeta,
  PmMinutes,
  PmOffPathForm,
  PmOffPathGroup,
  PmPain,
  PmPainCause,
  PmPainCell,
  PmQuoteMail,
  PmReplySplit,
  PmResponse,
  PmReworkPart,
  PmRuns,
  PmScoreRow,
  PmSharedFile,
  PmSlaBand,
  PmSpeedBand,
  PmSummary,
  PmTimelineEvent,
  PmTimeSplit,
  PmToolLift,
  PmToolPresence,
  PmTools,
  PmTrend,
  PmTrendPoint,
  PmUpside,
  PmValue,
  PmValueStream,
  PmVariant,
  PmVariants,
  PmWeek,
} from "./generated";

/** The closed lists of the API, read from the generated shapes. */
export type PmActivity = PmMapNode["activity"];
export type PmStatus = PmMeasure["status"];

/** The rule of a finding (16 Oct 2026), a closed list of the API. */
export type PmFindingRule = PmFinding["rule"];
export type PmFindingOwner = PmFinding["owner"];
export type PmFindingStrength = PmFinding["strength"];

/** The pain point of a finding (16 Oct 2026, the redesign), a closed list of the API. */
export type PmPainPoint = PmPain["pain_point"];
export type PmSeverity = PmFinding["severity"];
export type PmWeakReason = NonNullable<PmFinding["weak_reasons"]>[number];
export type PmTrendDirection = NonNullable<PmTrend["direction"]>;
export type PmTrendReason = NonNullable<PmTrend["reason"]>;
/** The link tier of a case (16 Oct 2026, round 4): how sure the inquiry to quotation link is. */
export type PmLinkTier = PmCaseRow["link_tier"];
/** How a figure is known (round 4): measured, a lower bound, indicated, assumed, not measured. */
export type PmBasis = PmReworkPart["basis"];
export type PmScoreUnit = PmScoreRow["unit"];
/** Round 6 (V1): a pain point or a lever is in the I2Q scope, needs a decision, or is not I2Q. */
export type PmI2qFit = NonNullable<PmPain["i2q_fit"]>;
/** Round 6 (F2): the validity class of an area's quotations (30 days, 90 days, other). */
export type PmValidityClass = NonNullable<PmDecision["validity_class"]>;
/** The quotation type of the shared filter (round 4). */
export type PmQuotationType = "all" | "Sales" | "Project";
/** The sort of the case list (round 4). */
export type PmCaseSort = "opened_desc" | "elapsed_desc" | "value_desc";
/** The action code of a finding (`PmFindingAction`), a closed list of the API. */
export type PmFindingAction = PmFinding["action"];

// ─── the core model (pm8, 17 Oct 2026) ────────────────────────────────────────
//
// The shapes are the generated types of `api/models.py` `PmCoreModel` and its rows.
// `PmFindings.core_model` carries them on the answer of `GET /process-mining/findings`
// (no new route). The names below are the closed lists inside those shapes.

/** An F&O form group of the NOVA import (`PmFormWindowGroup`). */
export type PmFormWindowGroup = Exclude<PmFormWindowIn["form_group"], "all">;
/** The cause of a pain point in the core model. */
export type PmCause = PmCausePartRow["cause"];
/** The part of a pain point that a cause covers. */
export type PmCausePart = PmCausePartRow["part"];
/** The role that owns a cause. */
export type PmCauseOwner = PmPainCause["cause_owner"];
/** The track of a 9 Nov action against the core model. */
export type PmActionTrackCode = PmActionTrack["track"];
/** How a golden step is linked to its F&O form. */
export type PmGoldenLink = PmGoldenStep["link"];
/** The stage of a golden step. */
export type PmGoldenStage = PmGoldenStep["stage"];
/** A golden step in the create windows of one area. */
export type PmGoldenState = PmGoldenUse["state"];
/** Inferred until the core model owner confirms the causes. */
export type PmCauseBasis = PmPainCause["basis"];

/**
 * The core model block of a findings answer: `null` when the server could not build it, or
 * when an older answer has no such key.
 */
export function coreModelOf(findings: PmFindings | null | undefined): PmCoreModel | null {
  return findings?.core_model ?? null;
}

// The last one of the block: tests/usage-capture.test.mjs reads a name that ends in "Key"
// followed by `["..."` as a query key, so no indexed access follows it.
export type PmEvidenceKey = PmFindingEvidence extends { key: infer Key } ? Key : never;

/** The filters every figure route takes (the bar at the top of the page). */
export type PmFilters = {
  /** Role codes; empty means every area. */
  areas: string[];
  /** The window in days, ending today. */
  days: 30 | 90 | 120;
  kind: "all" | "inquiry" | "quotation";
  /** The quotation type (round 4); left out means every type. */
  qtype?: Exclude<PmQuotationType, "all">;
  /** A period of ISO days (round 4); when both are set they win over `days`. */
  from?: string;
  to?: string;
};

/** The filters of the case list (Layer 5). */
export type PmCaseQuery = {
  variantId?: string;
  activity?: PmActivity;
  rework?: boolean;
  /** The cases of one finding (`<rule>:<area>:<subject>`, 16 Oct 2026). */
  finding?: string;
  /** Round 4: the link tier, one pain point, the sort and a quotation number prefix. */
  linkTier?: PmLinkTier;
  pain?: PmPainPoint;
  sort?: PmCaseSort;
  q?: string;
  offset: number;
};

/** The shape of a finding id, as the case route takes it. */
export const PM_FINDING_ID_SHAPE = /^[a-z_]{3,32}:[a-z]{2,4}:[A-Za-z0-9_]{1,64}$/;

/** The figures change at most once a night: keep them 5 minutes. */
const FIGURES_STALE_MS = 5 * 60_000;

/**
 * The statuses of a server that is busy or restarts (round 6, O11): a gateway error or a
 * time-out while the figures are calculated. Only these are tried again, one time.
 */
export const PM_RETRY_STATUSES: readonly number[] = [502, 503, 504];
/** The wait before the one retry. */
export const PM_RETRY_DELAY_MS = 5000;

/** The HTTP status of a failed request, or undefined. */
export function pmStatusOf(error: unknown): number | undefined {
  return (error as Partial<ApiError> | null)?.status;
}

/** True when a failed request of a process-mining route is worth one more try. */
export function shouldRetryPm(status: number | undefined): boolean {
  return status != null && PM_RETRY_STATUSES.includes(status);
}

/** The retry rule of every process-mining hook: one retry on 502, 503 or 504, never on 4xx. */
function pmRetry(failureCount: number, error: unknown): boolean {
  return failureCount < 1 && shouldRetryPm(pmStatusOf(error));
}
const PM_RETRY = { retry: pmRetry, retryDelay: PM_RETRY_DELAY_MS } as const;
/** Cases per page of the case list. */
export const PM_CASE_PAGE = 50;
/**
 * The sort of the case list when the address names none (round 6, U7): the cases with the
 * most value first, so page 1 is useful (newest first opened on inquiries with no link).
 */
export const PM_CASE_DEFAULT_SORT: PmCaseSort = "value_desc";

/** `2026-10-15` of a date, in UTC. */
function isoDay(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** The query of a figure route (see `pmApiQuery`). */
export type PmApiQuery = {
  area?: string[];
  date_from: string;
  date_to: string;
  kind: PmFilters["kind"];
  qtype?: Exclude<PmQuotationType, "all">;
};

/**
 * The query of a figure route: the areas, the period and the kind, and the quotation type
 * when one is picked. The period is `from` to `to` when both are set (round 4), else the
 * window ending today. A window the server does not hold yet (after the last build) shows as
 * an empty end, not an error.
 */
export function pmApiQuery(filters: PmFilters, today: Date = new Date()): PmApiQuery {
  const window = new Date(today.getTime() - filters.days * 86_400_000);
  const period = filters.from && filters.to;
  return {
    ...(filters.areas.length > 0 ? { area: [...filters.areas] } : {}),
    date_from: period ? (filters.from ?? "") : isoDay(window),
    date_to: period ? (filters.to ?? "") : isoDay(today),
    kind: filters.kind,
    ...(filters.qtype ? { qtype: filters.qtype } : {}),
  };
}

/** The query of the case list and of its CSV: the figure query and the case filters. */
export function pmCaseApiQuery(filters: PmFilters, cases: PmCaseQuery, today?: Date) {
  return {
    ...pmApiQuery(filters, today),
    variant_id: cases.variantId,
    activity: cases.activity,
    rework: cases.rework,
    finding: cases.finding,
    link_tier: cases.linkTier,
    pain_point: cases.pain,
    sort: cases.sort ?? PM_CASE_DEFAULT_SORT,
    q: cases.q,
  };
}

/**
 * The address of the case CSV for the filters of the page (`GET /process-mining/case-export`,
 * round 4). The browser downloads the file with the session cookie; nothing is sent elsewhere.
 */
export function pmCaseExportHref(filters: PmFilters, cases: PmCaseQuery, today?: Date): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(pmCaseApiQuery(filters, cases, today))) {
    if (value == null || value === "") {
      continue;
    }
    if (Array.isArray(value)) {
      for (const entry of value) {
        query.append(key, String(entry));
      }
    } else {
      query.set(key, String(value));
    }
  }
  return `/api/process-mining/case-export?${query.toString()}`;
}

/**
 * The findings and their pain points (16 Oct 2026): the detectors run again on every request
 * over the event log and the imports (about 13 s); the answer holds codes and numbers, the
 * page makes the sentences. The tab "Pain points" always asks for every area and kind and
 * filters the areas in the browser, so a change of area costs no new request. The previous
 * answer stays on screen while a new window loads.
 */
export function useProcessMiningFindings(filters: PmFilters, options: { enabled: boolean }) {
  return useQuery({
    queryKey: ["process-mining-findings", filters],
    queryFn: async (): Promise<PmFindings | null> => {
      const { data } = await getProcessMiningFindings({
        query: pmApiQuery(filters),
        throwOnError: true,
      });
      return data ?? null;
    },
    enabled: options.enabled,
    ...PM_RETRY,
    staleTime: FIGURES_STALE_MS,
    placeholderData: keepPreviousData,
  });
}

/** Layer 1: the value stream. */
export function useProcessMiningSummary(filters: PmFilters, options: { enabled: boolean }) {
  return useQuery({
    queryKey: ["process-mining-summary", filters],
    queryFn: async (): Promise<PmSummary | null> => {
      const { data } = await getProcessMiningSummary({
        query: pmApiQuery(filters),
        throwOnError: true,
      });
      return data ?? null;
    },
    enabled: options.enabled,
    ...PM_RETRY,
    staleTime: FIGURES_STALE_MS,
  });
}

/** Layer 2: the process map; `minShare` hides the rare paths (0 to 0.5). */
export function useProcessMiningMap(
  filters: PmFilters,
  minShare: number,
  options: { enabled: boolean },
) {
  return useQuery({
    queryKey: ["process-mining-map", filters, minShare],
    queryFn: async (): Promise<PmMap | null> => {
      const { data } = await getProcessMiningMap({
        query: { ...pmApiQuery(filters), min_share: minShare },
        throwOnError: true,
      });
      return data ?? null;
    },
    enabled: options.enabled,
    ...PM_RETRY,
    staleTime: FIGURES_STALE_MS,
    placeholderData: (previous) => previous,
  });
}

/** Layer 2: the most frequent variants. */
export function useProcessMiningVariants(
  filters: PmFilters,
  limit: number,
  options: { enabled: boolean },
) {
  return useQuery({
    queryKey: ["process-mining-variants", filters, limit],
    queryFn: async (): Promise<PmVariants | null> => {
      const { data } = await listProcessMiningVariants({
        query: { ...pmApiQuery(filters), limit },
        throwOnError: true,
      });
      return data ?? null;
    },
    enabled: options.enabled,
    ...PM_RETRY,
    staleTime: FIGURES_STALE_MS,
  });
}

/** Layer 3: the areas side by side. */
export function useProcessMiningAreas(filters: PmFilters, options: { enabled: boolean }) {
  return useQuery({
    queryKey: ["process-mining-areas", filters],
    queryFn: async (): Promise<PmAreaComparison | null> => {
      const { data } = await getProcessMiningAreas({
        query: pmApiQuery(filters),
        throwOnError: true,
      });
      return data ?? null;
    },
    enabled: options.enabled,
    ...PM_RETRY,
    staleTime: FIGURES_STALE_MS,
  });
}

/** Layer 4: the tools between the steps (from the by-hand imports). */
export function useProcessMiningTools(filters: PmFilters, options: { enabled: boolean }) {
  return useQuery({
    queryKey: ["process-mining-tools", filters],
    queryFn: async (): Promise<PmTools | null> => {
      const { data } = await getProcessMiningTools({
        query: pmApiQuery(filters),
        throwOnError: true,
      });
      return data ?? null;
    },
    enabled: options.enabled,
    ...PM_RETRY,
    staleTime: FIGURES_STALE_MS,
  });
}

/** Layer 5: the case list, filtered by variant, step or rework. */
export function useProcessMiningCases(
  filters: PmFilters,
  cases: PmCaseQuery,
  options: { enabled: boolean },
) {
  return useQuery({
    queryKey: ["process-mining-cases", filters, cases],
    queryFn: async (): Promise<PmCaseList | null> => {
      const { data } = await listProcessMiningCases({
        query: {
          ...pmCaseApiQuery(filters, cases),
          limit: PM_CASE_PAGE,
          offset: cases.offset,
        },
        throwOnError: true,
      });
      return data ?? null;
    },
    enabled: options.enabled,
    ...PM_RETRY,
    staleTime: FIGURES_STALE_MS,
    placeholderData: (previous) => previous,
  });
}

/** The shape of a case key (`i-<case id>` or `q-<entity>-<quotation id>`). */
export const PM_CASE_KEY_SHAPE = /^(i-\d+|q-[a-z]+-[A-Za-z0-9-]+)$/;

/** Layer 5: one case as a timeline. */
export function useProcessMiningCase(caseKey: string, options: { enabled: boolean }) {
  return useQuery({
    queryKey: ["process-mining-case", caseKey],
    queryFn: async (): Promise<PmCaseTimeline | null> => {
      const { data } = await getProcessMiningCase({
        path: { case_key: caseKey },
        throwOnError: true,
      });
      return data ?? null;
    },
    enabled: options.enabled && PM_CASE_KEY_SHAPE.test(caseKey),
    ...PM_RETRY,
    staleTime: FIGURES_STALE_MS,
  });
}

/** "Where I2Q helps": the baseline and the steps I2Q takes over. */
export function useProcessMiningBaseline(filters: PmFilters, options: { enabled: boolean }) {
  return useQuery({
    queryKey: ["process-mining-baseline", filters],
    queryFn: async (): Promise<PmBaseline | null> => {
      const { data } = await getProcessMiningBaseline({
        query: pmApiQuery(filters),
        throwOnError: true,
      });
      return data ?? null;
    },
    enabled: options.enabled,
    ...PM_RETRY,
    staleTime: FIGURES_STALE_MS,
  });
}

/**
 * The value answer (16 Oct 2026, round 4): the headline of the summary, the speed and the
 * decision figures, the benchmarks, the weekly series, the funnel and the I2Q scorecard. The
 * summary above the tabs asks for it on every tab; the server caches it per build.
 */
export function useProcessMiningValue(filters: PmFilters, options: { enabled: boolean }) {
  return useQuery({
    queryKey: ["process-mining-value", filters],
    queryFn: async (): Promise<PmValue | null> => {
      const { data } = await getProcessMiningValue({
        query: pmApiQuery(filters),
        throwOnError: true,
      });
      return data ?? null;
    },
    enabled: options.enabled,
    ...PM_RETRY,
    staleTime: FIGURES_STALE_MS,
    placeholderData: keepPreviousData,
  });
}

/** The builds and the imports, with the state of the pseudonym key. */
export function useProcessMiningRuns(options: { enabled: boolean }) {
  return useQuery({
    queryKey: ["process-mining-runs"],
    queryFn: async (): Promise<PmRuns | null> => {
      const { data } = await listProcessMiningRuns({ throwOnError: true });
      return data ?? null;
    },
    enabled: options.enabled,
    ...PM_RETRY,
    staleTime: FIGURES_STALE_MS,
  });
}

// ─── The report download (17 Oct 2026, pm7) ──────────────────────────────────
//
// `PmReportStatus` and `PmReportTextsRequest` come from the generated client (`api/models.py`).
// The audience, format and state literals are inline in the schema, so the named types below
// are taken from the generated types and cannot drift from the server.

export type { PmReportStatus, PmReportTextsRequest };

/** The four audiences of the report (`PmReportAudience`). */
export type PmReportAudience = PmReportStatus["audience"];
/** The two file formats (`PmReportFormat`). */
export type PmReportFormat = NonNullable<DownloadProcessMiningReportData["query"]["format"]>;
/**
 * The text state of a report (`PmReportTextState`): ready (AI text that passed the checks),
 * writing, missing (no text yet), standard (the last try gave standard text), off (AI writing
 * is off on this server).
 */
export type PmReportTextState = PmReportStatus["state"];

/** The audiences in the order of the dialog. */
export const PM_REPORT_AUDIENCES: readonly PmReportAudience[] = [
  "steering_group",
  "sales_leaders",
  "area",
  "process_it",
];
/** The formats in the order of the dialog. */
export const PM_REPORT_FORMATS: readonly PmReportFormat[] = ["docx", "pptx"];
/** While the text is being written, the status is read again every 15 seconds. */
export const PM_REPORT_POLL_MS = 15_000;

/** The area of a report request: only the audience "area" takes one. */
function reportArea(audience: PmReportAudience, area: string | null): string | null {
  return audience === "area" && area ? area : null;
}

/**
 * The address of the report file (`GET /process-mining/report`). The browser downloads the
 * file with the session cookie. The area goes only with the audience "area". The filters of
 * the page never go: the report uses the default figures of the last build.
 */
export function pmReportHref(
  audience: PmReportAudience,
  area: string | null,
  format: PmReportFormat,
): string {
  const query = new URLSearchParams();
  query.set("audience", audience);
  const scoped = reportArea(audience, area);
  if (scoped) {
    query.set("area", scoped);
  }
  query.set("format", format);
  return `/api/process-mining/report?${query.toString()}`;
}

/** The query key of the report status; its first part is a request key of the usage log. */
export const pmReportStatusKey = (audience: PmReportAudience, area: string | null) => [
  "process-mining-report-status",
  audience,
  reportArea(audience, area),
];

/** `GET /process-mining/report/status`: never calls the model, never waits for it. */
export async function getPmReportStatus(
  audience: PmReportAudience,
  area: string | null,
): Promise<PmReportStatus | null> {
  const scoped = reportArea(audience, area);
  const { data } = await getProcessMiningReportStatus({
    query: scoped ? { audience, area: scoped } : { audience },
    throwOnError: true,
  });
  return data ?? null;
}

/** `POST /process-mining/report/texts`: the server writes the text in the background. */
export async function postPmReportTexts(
  body: PmReportTextsRequest,
): Promise<PmReportStatus | null> {
  const scoped = reportArea(body.audience, body.area ?? null);
  const { data } = await writeProcessMiningReportTexts({
    body: { audience: body.audience, area: scoped },
    throwOnError: true,
  });
  return data ?? null;
}

/** The poll rule of the status: every 15 seconds while the text is being written, else never. */
export function pmReportRefetchInterval(state: PmReportTextState | undefined): number | false {
  return state === "writing" ? PM_REPORT_POLL_MS : false;
}

/**
 * The text state of one report while the dialog "Download report" is open. It is read again
 * every 15 seconds while the state is "writing".
 */
export function useProcessMiningReportStatus(
  audience: PmReportAudience,
  area: string | null,
  enabled: boolean,
) {
  return useQuery({
    queryKey: pmReportStatusKey(audience, area),
    queryFn: () => getPmReportStatus(audience, area),
    enabled: enabled && (audience !== "area" || reportArea(audience, area) !== null),
    ...PM_RETRY,
    staleTime: 0,
    refetchInterval: (query) => pmReportRefetchInterval(query.state.data?.state),
  });
}

/**
 * Asks the server to write the text of one report. The answer is the new status (usually
 * "writing"); it replaces the cached status, so the poll starts at once.
 */
export function useWriteProcessMiningReportTexts() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["process-mining-report-status", "write"],
    mutationFn: (body: PmReportTextsRequest) => postPmReportTexts(body),
    onSuccess: (status, body) => {
      if (status) {
        queryClient.setQueryData(pmReportStatusKey(body.audience, body.area ?? null), status);
      }
    },
  });
}

/** What a failed process-mining request reads as. Raw server text never reaches the screen. */
export function processMiningFailureText(error: unknown): string {
  const { status } = (error ?? {}) as Partial<ApiError>;
  switch (status) {
    case 401:
      return "Your session has ended. Sign in again.";
    case 403:
      return "Your role does not include process mining.";
    case 404:
      return "This case is not in the current build.";
    case 422:
      return "The filter is not valid. Pick a window of at most 180 days.";
    case 502:
    case 503:
    case 504:
      return "The figures are being calculated. This can take up to 1 minute.";
    default:
      return "I2Q could not load these figures. Try again in a moment.";
  }
}
